import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/apiConfig';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png'; 
import ContactModal from './ContactModal'; 

const AuthForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false); 
    
    // On récupère la fonction login du contexte
    const { login, authToken } = useAuth(); 
    const navigate = useNavigate();

    // Redirection automatique si déjà connecté
    useEffect(() => {
        if (authToken && authToken.token) {
            // Si admin, on renvoie vers admin, sinon vers chat
            if (authToken.isAdmin) {
                navigate('/admin/users', { replace: true });
            } else {
                navigate('/', { replace: true });
            }
        }
    }, [authToken, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { data } = await axios.post(`${API_URL}/auth/login`, { email, password });
            
            // On extrait les données reçues du serveur
            const { token, userName, userId, userEmail, isAdmin } = data;

            // ✅ ON APPELLE LE CONTEXTE (C'est lui qui va gérer le localStorage)
            login(token, userId, userName, userEmail, isAdmin);

            // La redirection se fera automatiquement grâce au useEffect ci-dessus
            // ou on peut la forcer ici pour être plus réactif :
             if (isAdmin) {
                navigate('/admin/users', { replace: true });
            } else {
                navigate('/', { replace: true });
            }

        } catch (err) {
            console.error("Erreur login:", err);
            setError(err.response?.data?.msg || 'Identifiants incorrects');
            setLoading(false); // On n'arrête le chargement qu'en cas d'erreur (sinon on attend la redirection)
        }
    };

    const handleForgotPassword = () => {
        navigate('/forgot-password');
    };

    return (
        <div className="h-screen w-full flex items-center justify-center bg-[#f8f9fa] relative overflow-hidden font-sans">
            <div className="absolute top-0 left-0 w-full h-[55%] bg-[#1c2143] skew-y-[-4deg] transform origin-top-left -translate-y-24 z-0 shadow-xl"></div>

            <div className="relative z-10 w-full max-w-md px-6">
                <div className="bg-white rounded-2xl shadow-2xl p-10 border-t-4 border-[#Ceaa5b] flex flex-col items-center animate-fade-in-up">
                    
                    <div className="mb-8 w-full flex justify-center">
                        <img src={logo} alt="AI Expertys" className="h-24 w-auto object-contain" />
                    </div>

                    <h2 className="text-2xl font-bold text-[#1c2143] text-center mb-1">
                        Espace Expert
                    </h2>
                    <p className="text-gray-400 text-center mb-8 text-sm">
                        Connectez-vous pour accéder à Sophia
                    </p>

                    <form className="w-full space-y-5" onSubmit={handleSubmit}>
                        
                        <div className="group">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1 uppercase tracking-wider">Email</label>
                            <input
                                className="block w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 
                                focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#Ceaa5b] focus:border-transparent transition-all duration-200"
                                type="text"
                                placeholder="nom@aiexpertys.fr"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className="group">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1 uppercase tracking-wider">Mot de passe</label>
                            <input
                                className="block w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 
                                focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#Ceaa5b] focus:border-transparent transition-all duration-200"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 text-red-600 text-xs font-medium rounded-md border border-red-100 text-center flex items-center justify-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        )}

                        <button
                            className={`w-full py-3.5 px-4 rounded-lg shadow-lg text-sm font-bold text-white uppercase tracking-wider mt-4
                                ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1c2143] hover:bg-[#2a3055] hover:shadow-xl'} 
                                transition duration-300 transform active:scale-[0.98] flex justify-center items-center`}
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? 'Connexion...' : 'Se connecter'}
                        </button>
                    </form>

                    <div className="flex flex-col items-center mt-6 space-y-2">
                        <button onClick={handleForgotPassword} className="text-xs text-gray-400 hover:text-[#Ceaa5b] transition-colors font-medium border-b border-transparent hover:border-[#Ceaa5b] pb-0.5">
                            Mot de passe oublié ?
                        </button>
                        
                        <div className="text-xs text-gray-400">
                            Problème de connexion ?{' '}
                            <button onClick={() => setShowContactModal(true)} className="text-[#1c2143] font-bold hover:underline">
                                Contactez le support
                            </button>
                        </div>
                    </div>
                </div>

                <div className="absolute -bottom-16 left-0 w-full text-center">
                     <p className="text-xs text-gray-400 opacity-60">
                        &copy; {new Date().getFullYear()} AI Expertys. Tous droits réservés.
                    </p>
                </div>
            </div>

            {showContactModal && (
                <ContactModal onClose={() => setShowContactModal(false)} />
            )}
            
        </div>
    );
};

export default AuthForm;